import type { Task } from "./types.js";

/** How completing/reopening a task propagates between it and its subtasks.
 *  ("ask" is a UI concern resolved to one of these before reaching core.) */
export type SubtaskRollupMode =
  | "two_way"
  | "children_to_parent"
  | "parent_to_children"
  | "none";

const isReal = (t: Task) => !t.virtual && !t.deleted_at;

/** Direct subtasks of `parentId` (active, non-virtual), ordered by their manual
 *  `subtask_order` then creation time (stable for equal orders / legacy rows). */
export function childrenOf(tasks: ReadonlyArray<Task>, parentId: string): Task[] {
  return tasks
    .filter((t) => isReal(t) && t.subtask_parent_id === parentId)
    .sort((a, b) => (a.subtask_order - b.subtask_order) || a.created_at.localeCompare(b.created_at));
}

/** Reorder so `draggedId` lands at `targetId`'s position among its siblings.
 *  Returns the sibling ids in their new order (caller assigns sequential
 *  `subtask_order`). Both must be children of the same parent. */
export function reorderedSiblingIds(
  tasks: ReadonlyArray<Task>,
  parentId: string,
  draggedId: string,
  targetId: string,
): string[] {
  const ids = childrenOf(tasks, parentId).map((t) => t.id);
  const from = ids.indexOf(draggedId);
  const to = ids.indexOf(targetId);
  if (from === -1 || to === -1 || from === to) return ids;
  ids.splice(from, 1);
  ids.splice(to, 0, draggedId);
  return ids;
}

/** All transitive descendant ids of `id` (excludes `id` itself). */
export function descendantIds(tasks: ReadonlyArray<Task>, id: string): string[] {
  const out: string[] = [];
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const c of childrenOf(tasks, cur)) {
      if (!out.includes(c.id)) { out.push(c.id); stack.push(c.id); }
    }
  }
  return out;
}

/** Done/total over a task's DIRECT children. total === 0 ⇒ no subtasks. */
export function subtaskProgress(
  tasks: ReadonlyArray<Task>,
  parentId: string,
): { done: number; total: number } {
  const kids = childrenOf(tasks, parentId);
  return { done: kids.filter((t) => t.status === "done").length, total: kids.length };
}

/** True if `ancestorId` is `taskId` itself or any ancestor of it (walks up the
 *  subtask_parent_id chain). Used as a cycle guard before re-parenting and to
 *  order parents ahead of descendants in nest layout. */
export function isAncestor(
  tasks: ReadonlyArray<Task>,
  ancestorId: string,
  taskId: string,
): boolean {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  let cur: Task | undefined = byId.get(taskId);
  const seen = new Set<string>();
  while (cur) {
    if (cur.id === ancestorId) return true;
    if (seen.has(cur.id)) break; // defend against pre-existing cycles
    seen.add(cur.id);
    cur = cur.subtask_parent_id ? byId.get(cur.subtask_parent_id) : undefined;
  }
  return false;
}

/**
 * Completion roll-up effect for toggling task `changedId` to `done`. Returns the
 * OTHER task ids that should change as a result (the caller has already applied
 * `changedId` itself). Pure — `tasks` should already reflect `changedId`'s new
 * status so child-completeness checks see the latest state.
 */
export function computeRollup(
  tasks: ReadonlyArray<Task>,
  changedId: string,
  done: boolean,
  mode: SubtaskRollupMode,
): { complete: string[]; reopen: string[] } {
  const complete: string[] = [];
  const reopen: string[] = [];
  if (mode === "none") return { complete, reopen };
  const byId = new Map(tasks.map((t) => [t.id, t]));

  // parent → children: cascade the parent's new state down to every descendant.
  if (mode === "two_way" || mode === "parent_to_children") {
    for (const id of descendantIds(tasks, changedId)) {
      const t = byId.get(id);
      if (!t) continue;
      if (done && t.status !== "done") complete.push(id);
      if (!done && t.status === "done") reopen.push(id);
    }
  }

  // children → parent: walk ancestors. Completing the last open child completes
  // the parent; reopening a child reopens a done parent.
  if (mode === "two_way" || mode === "children_to_parent") {
    const completing = new Set(complete);
    const reopening = new Set(reopen);
    let cur = byId.get(changedId)?.subtask_parent_id ?? null;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const parent = byId.get(cur);
      if (!parent) break;
      if (done) {
        const kids = childrenOf(tasks, cur);
        const allDone = kids.every(
          (k) => k.status === "done" || completing.has(k.id) || k.id === changedId,
        );
        if (allDone && parent.status !== "done" && !completing.has(cur)) {
          complete.push(cur);
          completing.add(cur);
        } else break; // a parent that isn't fully done stops the upward cascade
      } else if (parent.status === "done" && !reopening.has(cur)) {
        reopen.push(cur);
        reopening.add(cur);
      }
      cur = parent.subtask_parent_id;
    }
  }

  return { complete, reopen };
}
